/** @jsx jsx */
import { jsx } from '@emotion/core';
import axios from 'axios';
import React, { Component } from 'react';
import Button from '../ui/Button';
import { adalApiFetch } from '../AdalConfig';
import colors from '../../styles/colors';

// #region css
const domainSectionStyle = {
  padding: 24,
  backgroundColor: '#f8f9fa',
  borderRadius: 8,
  '@media print': { display: 'none' },
};

const sectionTitle = {
  fontSize: 32,
  fontWeight: 600,
  marginBottom: 16,
  color: colors.grayDark,
};

const inputWrap = {
  display: 'flex',
  gap: 16,
  alignItems: 'center',
  marginBottom: 24,
  '@media(max-width: 767px)': {
    flexDirection: 'column',
  },
};

const inputStyle = {
  flex: 1,
  minWidth: 0,
  backgroundColor: 'white',
  height: 56,
  padding: '0 16px',
  fontSize: 16,
  border: `2px solid ${colors.gray}`,
  borderRadius: 5,
  outline: 'none',
  transition: '.2s border',
  ':focus': {
    border: `2px solid ${colors.blueSecondary}`,
  },
  '::placeholder': {
    color: colors.grayMed,
  },
};

const buttonStyle = {
  height: 56,
  minWidth: 160,
  '@media(max-width: 767px)': {
    width: '100%',
  },
};

const resultCard = {
  backgroundColor: 'white',
  padding: 24,
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
};

const resultRow = {
  display: 'flex',
  marginBottom: 12,
  fontSize: 16,
  '@media(max-width: 767px)': {
    flexDirection: 'column',
  },
};

const resultLabel = {
  fontWeight: 600,
  marginRight: 8,
  minWidth: 180,
  color: colors.grayDark,
};

const resultValue = {
  color: colors.grayMed,
};

const warningBox = {
  padding: 16,
  borderRadius: 8,
  marginTop: 16,
  backgroundColor: '#fff3cd',
  border: '1px solid #ffc107',
  color: '#856404',
  fontSize: 16,
  fontWeight: 500,
};

const errorBox = {
  padding: 16,
  borderRadius: 8,
  marginTop: 16,
  backgroundColor: '#f8d7da',
  border: '1px solid #dc3545',
  color: '#721c24',
  fontSize: 16,
};

// #endregion

export default class DomainVerificationContent extends Component {
  state = {
    domainName: '',
    isLoading: false,
    domainResult: null,
    error: null,
  };

  handleInputChange = e => {
    this.setState({
      domainName: e.target.value,
      error: null,
    });
  };

  handleKeyPress = e => {
    if (e.key === 'Enter') {
      this.handleVerifyDomain();
    }
  };

  handleVerifyDomain = async () => {
    const { domainName } = this.state;

    if (!domainName.trim()) {
      this.setState({ error: 'Please enter a domain name' });
      return;
    }

    this.setState({ isLoading: true, error: null, domainResult: null });

    try {
      const { data } = await adalApiFetch(
        axios.get,
        `api/Domain/VerifyDomain?domainName=${encodeURIComponent(domainName)}`,
        {},
      );

      this.setState({
        domainResult: data,
        isLoading: false,
      });
    } catch (error) {
      const errorMessage =
        error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : 'Failed to verify domain. Please try again.';

      this.setState({
        error: errorMessage,
        isLoading: false,
      });
    }
  };

  formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1);
    const day = String(date.getDate());
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  renderResult = () => {
    const { domainResult } = this.state;

    if (!domainResult) return null;

    const domainAgeInDays = domainResult.domainAgeInDays;
    const showWarning = (domainAgeInDays !== null && domainAgeInDays < 365) || domainAgeInDays === null;

    let warningMessage = '';
    if (domainAgeInDays === null) {
      warningMessage =
        'Unable to determine domain age. This domain should be verified carefully as the creation date is unknown.';
    } else if (domainAgeInDays < 365) {
      warningMessage = `This domain is less than 1 year old (${domainAgeInDays} days). Newly registered domains may be suspicious and should be verified carefully.`;
    }

    return (
      <div css={resultCard}>
        <div css={resultRow}>
          <span css={resultLabel}>Domain Name:</span>
          <span css={resultValue}>{domainResult.domainName}</span>
        </div>
        {domainResult.createdDate && (
          <div css={resultRow}>
            <span css={resultLabel}>Created Date:</span>
            <span css={resultValue}>{this.formatDate(domainResult.createdDate)}</span>
          </div>
        )}
        {domainAgeInDays !== null ? (
          <div css={resultRow}>
            <span css={resultLabel}>Domain Age:</span>
            <span css={resultValue}>
              {domainAgeInDays} days ({Math.floor(domainAgeInDays / 365)} years,{' '}
              {Math.floor((domainAgeInDays % 365) / 30)} months)
            </span>
          </div>
        ) : (
          <div css={resultRow}>
            <span css={resultLabel}>Domain Age:</span>
            <span css={resultValue}>Unknown</span>
          </div>
        )}
        {domainResult.registrar && (
          <div css={resultRow}>
            <span css={resultLabel}>Registrar:</span>
            <span css={resultValue}>{domainResult.registrar}</span>
          </div>
        )}

        {showWarning && (
          <div css={warningBox}>
            <strong>Warning: </strong>
            {warningMessage}
          </div>
        )}
      </div>
    );
  };

  render() {
    const { domainName, isLoading, error } = this.state;

    return (
      <div css={domainSectionStyle}>
        <h2 css={sectionTitle}>Domain Verification</h2>
        <p css={{ marginBottom: 24, color: colors.grayMed, fontSize: 16 }}>
          Verify domain age to check if a domain is trustworthy. Newly registered domains may be suspicious.
        </p>

        <div css={inputWrap}>
          <input
            css={inputStyle}
            type="text"
            value={domainName}
            onChange={this.handleInputChange}
            onKeyPress={this.handleKeyPress}
            placeholder="Enter domain name (e.g., google.com)"
            disabled={isLoading}
          />
          <Button styleoverrides={buttonStyle} onClick={this.handleVerifyDomain} disabled={isLoading}>
            {isLoading ? 'Verifying...' : 'Verify Domain'}
          </Button>
        </div>

        {error && <div css={errorBox}>{error}</div>}

        {this.renderResult()}
      </div>
    );
  }
}
